import type { ClassSession } from "@/types/ClassSession";
import { create } from "zustand";

export type WidgetView =
  | "calendar"
  | "packages"
  | "login"
  | "password"
  | "profile"
  | "booking"
  | "confirmation"
  | "private-booking"
  | "videos"
  | "video"
  | "programme";

const VIEW_ORDER: Record<WidgetView, number> = {
  calendar: 0,
  packages: 0,
  login: 1,
  password: 2,
  profile: 2,
  booking: 1,
  confirmation: 2,
  "private-booking": 0,
  videos: 0,
  video: 1,
  programme: 1,
};

type NavigateOptions = {
  intended?: WidgetView;
  profileTab?: string;
};

type ViewState = {
  current: WidgetView;
  previous: WidgetView | null;
  direction: 1 | -1;
  intended?: WidgetView;
  profileTab: string | null;
  selectedEvent: ClassSession | null;
  bookingId: string | null;
  /** data-layout="videos": the video or programme open. */
  videoSlug: string | null;
  programmeSlug: string | null;

  setSelectedEvent: (event: ClassSession | null, isConnected: boolean) => void;
  navigate: (view: WidgetView, opts?: NavigateOptions) => void;
  onBookingSuccess: (bookingId: string) => void;
  onLoginSuccess: () => void;
  openVideo: (slug: string) => void;
  openProgramme: (slug: string) => void;
};

export const useViewStore = create<ViewState>((set, get) => ({
  current: "calendar",
  previous: null,
  selectedEvent: null,
  bookingId: null,
  videoSlug: null,
  programmeSlug: null,
  profileTab: null,
  direction: 1,

  navigate: (target, opts) => {
    const current = get().current;

    const next = target;
    const direction = VIEW_ORDER[next] > VIEW_ORDER[current] ? 1 : -1;

    set({
      previous: current,
      current: next,
      intended: opts?.intended,
      profileTab: opts?.profileTab ?? null,
      direction,
    });
  },

  onLoginSuccess: () => {
    const { intended, current } = get();
    const next = intended ?? "calendar";

    const direction = VIEW_ORDER[next] > VIEW_ORDER[current] ? 1 : -1;

    set({
      previous: current,
      current: next,
      intended: undefined,
      direction,
    });
  },

  setSelectedEvent: (event, isConnected) =>
    set({
      selectedEvent: event,
      previous: get().current,
      current: isConnected ? "booking" : "login",
      // Sans cela, apres la connexion l'eleve retombait sur le planning au
      // lieu du cours qu'elle venait d'ouvrir.
      intended: isConnected ? undefined : "booking",
    }),

  openVideo: (slug) => {
    set({ videoSlug: slug });
    get().navigate("video");
  },

  openProgramme: (slug) => {
    set({ programmeSlug: slug });
    get().navigate("programme");
  },

  onBookingSuccess: (bookingId) =>
    set({
      bookingId,
      previous: get().current,
      current: "confirmation",
      direction: 1,
    }),
}));
